// API сервис для работы с бэкендом

// Все запросы идут в backend-shop (prize.mda-platform.top/api). Основной бэкенд
// (webapp.mda-platform.top) вызывается только из backend-shop: сервисные токены в браузер не попадают.
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://prize.mda-platform.top/api';
const SHOP_PANEL_API_URL = process.env.REACT_APP_SHOP_PANEL_API_URL || API_BASE_URL;
const CATALOG_API_URL = SHOP_PANEL_API_URL;

// Короткоживущий JWT магазина, выдаётся backend-shop после проверки подписи Telegram initData.
// Храним только в памяти вкладки: при перезапуске Mini App он получается заново.
let authToken = null;

export const setAuthToken = (token) => {
  authToken = token || null;
};

const authHeaders = (extra = {}) => ({
  'Content-Type': 'application/json',
  ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
  ...extra,
});

// Повторная авторизация по текущему initData (если JWT истёк)
const refreshAuth = async () => {
  const initData = window.Telegram?.WebApp?.initData;
  if (!initData) return false;
  try {
    const response = await fetch(`${API_BASE_URL}/validate-telegram`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ initData }),
    });
    if (!response.ok) return false;
    const data = await response.json();
    if (data.success && data.token) {
      setAuthToken(data.token);
      return true;
    }
  } catch (e) {
    console.error('Re-auth failed:', e);
  }
  return false;
};

// fetch с JWT; при 401 один раз обновляем токен и повторяем запрос
const authFetch = async (url, options = {}) => {
  const doFetch = () => fetch(url, { ...options, headers: authHeaders(options.headers || {}) });
  let response = await doFetch();
  if (response.status === 401 && await refreshAuth()) {
    response = await doFetch();
  }
  return response;
};

// Валидация Telegram WebApp данных: на сервер уходит только подписанная строка initData
export const validateTelegramData = async (telegramData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/validate-telegram`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        initData: telegramData.initData,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (data.success && data.token) {
      setAuthToken(data.token);
    }
    return data;
  } catch (error) {
    console.error('Error validating Telegram data:', error);
    throw error;
  }
};

// Получение информации о магазине
export const getShopInfo = async (chatId) => {
  try {
    const response = await authFetch(`${SHOP_PANEL_API_URL}/shop-panel/shop-info/${encodeURIComponent(chatId)}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting shop info:', error);
    throw error;
  }
};

// Получение статистики магазина
export const getShopStats = async (chatId) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/shop-stats/${encodeURIComponent(chatId)}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting shop stats:', error);
    throw error;
  }
};

// Проверка состояния API
export const checkApiHealth = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/health`);
    return response.ok;
  } catch (error) {
    console.error('API health check failed:', error);
    return false;
  }
};

// Сканирование QR-кода приза
export const scanPrize = async (qrData, chatId) => {
  try {
    // chatId сервер берёт из JWT; в теле больше не передаём
    const response = await authFetch(`${API_BASE_URL}/scan-prize`, {
      method: 'POST',
      body: JSON.stringify({
        qrData
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }

    const data = await response.json();
    console.log('📊 API response:', JSON.stringify(data, null, 2));
    
    // Если данные вложены в data.data, извлекаем их
    if (data.success && data.data && data.data.data) {
      return {
        success: data.success,
        data: data.data.data
      };
    }
    
    // Проверяем, что данные приза не пустые
    if (data.success && data.data && data.data.prize && Object.keys(data.data.prize).length === 0) {
      console.log('❌ Empty prize data received');
      return {
        success: false,
        error: 'QR код не найден'
      };
    }
    
    return data;
  } catch (error) {
    console.error('❌ Error scanning prize:', error);
    throw error;
  }
};

// Списание приза через основной backend (1С + БД)
export const claimPrize = async (prizeId, selectedProductId, selectedModificationId, shopName, chatId, giftPrize, shopCode, comment = null) => {
  try {
    console.log('🎁 Отправка запроса на списание приза в BACKEND...');
    console.log('   - Prize ID:', prizeId);
    console.log('   - Shop Code:', shopCode);
    console.log('   - Product ID:', selectedProductId);
    console.log('   - Modification ID:', selectedModificationId);
    
    // Запрос идёт в backend-shop, который проксирует его в основной backend
    // (с сервисным токеном) — тот спишет товар в 1С и обновит статус приза в БД.
    // chatId/claimedBy сервер берёт из JWT.
    const response = await authFetch(`${SHOP_PANEL_API_URL}/shop-panel/claim-prize`, {
      method: 'POST',
      body: JSON.stringify({
        prizeId,
        claimedBy: chatId,
        chatId,
        giftPrize,
        selectedProductId,
        selectedModificationId,
        shopCode,
        comment: comment || `Списание приза через shop-panel (магазин: ${shopName})`
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }

    const data = await response.json();
    console.log('✅ Claim prize response from BACKEND:', JSON.stringify(data, null, 2));

    return data;
  } catch (error) {
    console.error('❌ Error claiming prize:', error);
    throw error;
  }
};

// Получение информации о товаре по ID
export const getProductInfo = async (productId, shopCode) => {
  try {
    const url = shopCode
      ? `${CATALOG_API_URL}/catalog/product/${encodeURIComponent(productId)}?shop_code=${encodeURIComponent(shopCode)}`
      : `${CATALOG_API_URL}/catalog/product/${encodeURIComponent(productId)}`;

    const response = await authFetch(url, {
      method: 'GET',
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }

    const data = await response.json();
    console.log('📊 Product info response:', JSON.stringify(data, null, 2));

    return data;
  } catch (error) {
    console.error('❌ Error getting product info:', error);
    throw error;
  }
};

// Получение модификаций товара по ID
export const getProductModifications = async (productId, shopCode) => {
  try {
    const url = shopCode
      ? `${CATALOG_API_URL}/catalog/product/${encodeURIComponent(productId)}/modifications?shop_code=${encodeURIComponent(shopCode)}`
      : `${CATALOG_API_URL}/catalog/product/${encodeURIComponent(productId)}/modifications`;

    const response = await authFetch(url, {
      method: 'GET',
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }

    const data = await response.json();
    console.log('📊 Product modifications response:', JSON.stringify(data, null, 2));

    return data;
  } catch (error) {
    console.error('❌ Error getting product modifications:', error);
    throw error;
  }
};


// Получение характеристик товара по ID (для обратной совместимости)
export const getProductCharacteristics = async (productId, shopCode) => {
  try {
    const url = shopCode
      ? `${CATALOG_API_URL}/catalog/product/${encodeURIComponent(productId)}/characteristics?shop_code=${encodeURIComponent(shopCode)}`
      : `${CATALOG_API_URL}/catalog/product/${encodeURIComponent(productId)}/characteristics`;

    const response = await authFetch(url, {
      method: 'GET',
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }

    const data = await response.json();
    console.log('📊 Product characteristics response:', JSON.stringify(data, null, 2));

    return data;
  } catch (error) {
    console.error('❌ Error getting product characteristics:', error);
    throw error;
  }
};

// Списание товара через Balance API (1С)
export const consumeProduct = async (consumptionData) => {
  try {
    console.log('🔄 Отправка запроса на списание товара через shop-panel backend:', consumptionData);

    const response = await authFetch(`${SHOP_PANEL_API_URL}/consume-product`, {
      method: 'POST',
      body: JSON.stringify(consumptionData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }

    const data = await response.json();
    console.log('📊 Consume product response:', JSON.stringify(data, null, 2));

    return data;
  } catch (error) {
    console.error('❌ Error consuming product:', error);
    throw error;
  }
};

// Получение списка магазинов с товарами для приза
export const getPrizeAvailableShops = async (prizeId) => {
  try {
    const response = await authFetch(`${CATALOG_API_URL}/roulette/prize-available-shops/${encodeURIComponent(prizeId)}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }

    const data = await response.json();
    console.log('📍 Prize available shops response:', JSON.stringify(data, null, 2));

    return data;
  } catch (error) {
    console.error('❌ Error getting prize available shops:', error);
    throw error;
  }
}; 